import { formatMoney } from '@/features/workspace-usage';

import type { TaskCost } from '../executionHistory';

export interface TaskCostFigureProps {
  cost: TaskCost;
  /**
   * What to show for a task that has never run. Nothing by default — the board
   * card leaves the slot empty — while a table column needs a mark so an empty
   * cell does not read as a rendering failure.
   */
  placeholder?: string;
}

/**
 * Why a task's cost reads the way it does (spec 089 FR-004).
 *
 * One sentence per outcome, used twice each: as the `title` for a pointer, and in
 * screen-reader-only text because a `title` is hover-only — touch and keyboard users
 * never see one and screen readers treat it inconsistently. FR-004 asks the card to
 * *say why*, and a tooltip alone says it to some readers.
 *
 * Named here rather than inline so the two copies cannot drift into two explanations.
 *
 * **Uses the board's vocabulary, not the contract's.** A run the service calls `running`
 * is "in progress" everywhere a person can see it, and `failed` is "error" — retired
 * words that `board-redesign.test.tsx` guards against reappearing. A caveat about cost
 * is still card text, so it speaks the way the rest of the card speaks.
 */
const COST_REASON = {
  exact: 'Spent on this task so far',
  atLeast:
    'At least this much — some runs have no cost we can state, so the real total is higher',
  unknown:
    'No run on this task has a cost we can state — unmeasured, unpriced, or still in progress',
} as const;

/**
 * A task's cost, as the board card and the Tasks page both show it.
 *
 * One component for both surfaces (spec 110 SC-002): the card and the list row
 * used to be able to disagree about the same task's money only because each
 * would have spelled it out on its own. Rendering through this cannot.
 *
 * `whitespace-nowrap` but deliberately not truncated: a clipped "at least"
 * turns a floor back into a figure, which is the exact failure this exists to
 * remove, reintroduced by CSS (089 FR-003).
 */
export function TaskCostFigure({ cost, placeholder }: TaskCostFigureProps) {
  if (cost.kind === 'none') {
    return placeholder ? (
      <span className="text-muted-foreground">{placeholder}</span>
    ) : null;
  }

  if (cost.kind === 'unknown') {
    return (
      <span className="whitespace-nowrap" title={COST_REASON.unknown}>
        Cost unknown
        {/*
          The reason, for readers a `title` never reaches — it is hover-only,
          so touch and keyboard users get nothing from it and screen readers
          treat it inconsistently. FR-004 asks the card to *say why*, and a
          tooltip says it to some people. This is the same sentence, in the
          accessible name.
        */}
        <span className="sr-only"> — {COST_REASON.unknown}</span>
      </span>
    );
  }

  return (
    <span
      className="tabular-nums whitespace-nowrap"
      title={cost.kind === 'atLeast' ? COST_REASON.atLeast : COST_REASON.exact}
    >
      {/*
        The qualifier is a symbol and a sentence, never a colour or a weight
        (FR-006). A styling difference is not a statement, and is invisible to
        anyone who cannot see it as one.
      */}
      {/* "≥" wherever a cost is shown (spec 130 FR-005): it fits the card's
          one-line footer, and the card and the Tasks list must read alike
          (spec 110 SC-002). The words stay in the tooltip and the accessible
          name, so the floor is never colour or shape alone. */}
      {cost.kind === 'atLeast' ? <span aria-hidden="true">≥ </span> : null}
      {cost.kind === 'atLeast' ? (
        <span className="sr-only">{COST_REASON.atLeast} — </span>
      ) : null}
      {formatMoney(cost.amount, cost.currency, {
        // A task whose runs each cost a fraction of a cent must not render
        // `$0.00` — that is the string reserved for "this genuinely cost
        // nothing", and the two colliding is the defect this exists to fix,
        // reintroduced by truncation at the last step.
        showBelowCent: true,
      })}
    </span>
  );
}
